import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIChat = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedMRS, setSelectedMRS] = useState('');
  const [mrsList, setMrsList] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMRSList();
    // Generate new session ID
    setSessionId(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    
    // Add welcome message
    setMessages([{
      role: 'assistant',
      content: `Hello Dr. ${user?.name?.split(' ')[0] || 'Doctor'}! I'm your AI assistant for medical data analytics. I can help you with:

• **Clinical Data Analysis**: Analyze patient outcomes and therapy patterns
• **Adverse Event Detection**: Identify safety signals and risk factors  
• **Guideline Benchmarking**: Compare your data against clinical standards
• **Research Insights**: Generate publication-ready summaries
• **Statistical Analysis**: Perform cohort comparisons and trend analysis

Select a Medical Record Set below to get started, or ask me any general clinical research questions!`,
      timestamp: new Date().toISOString()
    }]);
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMRSList = async () => {
    try {
      const response = await axios.get(`${API}/mrs?user_id=${user.id}`);
      setMrsList(response.data);
    } catch (error) {
      console.error('Error fetching MRS list:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/chat?user_id=${user.id}`, {
        message: inputMessage,
        mrs_id: selectedMRS || null,
        session_id: sessionId
      });

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: response.data.timestamp
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    {
      category: "Clinical Analysis",
      questions: [
        "What are the most common adverse events in my dataset?",
        "Show me therapy success patterns",
        "Compare outcomes between different age groups"
      ]
    },
    {
      category: "Research Insights",
      questions: [
        "Generate a research summary for publication",
        "What are the key findings from this data?",
        "Create a statistical overview of the cohort"
      ]
    },
    {
      category: "Quality Assessment",
      questions: [
        "How does my data compare to clinical guidelines?",
        "Are there any data quality issues?",
        "What's the overall patient outcome distribution?"
      ]
    }
  ];

  const formatMessage = (content) => {
    // Basic markdown-style formatting
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/•/g, '•')
      .split('\n')
      .map((line, index) => (
        <div key={index} className="text-left" dangerouslySetInnerHTML={{ __html: line }} />
      ));
  };

  return (
    <div className="flex flex-col h-screen max-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Medical Assistant</h1>
            <p className="text-gray-600">Powered by GPT-4 for clinical data analysis</p>
          </div>
          
          {/* MRS Selector */}
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-gray-700">Dataset:</label>
            <select
              value={selectedMRS}
              onChange={(e) => setSelectedMRS(e.target.value)}
              className="form-input py-1 px-3 text-sm w-64"
            >
              <option value="">General Clinical Questions</option>
              {mrsList.map(mrs => (
                <option key={mrs.id} value={mrs.id}>
                  {mrs.name} ({mrs.patient_count} patients)
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {selectedMRS && (
          <div className="mt-2 p-2 bg-sky-50 rounded-lg">
            <div className="text-sm text-sky-800">
              <strong>Active Dataset:</strong> {mrsList.find(mrs => mrs.id === selectedMRS)?.name}
              <span className="ml-2 text-sky-600">
                ({mrsList.find(mrs => mrs.id === selectedMRS)?.specialty})
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`ai-chat-bubble ${
                message.role === 'user' ? 'ai-user-message' : 'ai-assistant-message'
              } text-left`}
              style={{ maxWidth: '70%' }}
            >
              {message.role === 'assistant' && (
                <div className="flex items-center space-x-2 mb-2">
                  <div className="h-6 w-6 bg-sky-600 rounded-full flex items-center justify-center">
                    <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-xs text-gray-600 font-medium">Exlva DARe AI</span>
                </div>
              )}
              
              <div className="prose prose-sm max-w-none text-left">
                {formatMessage(message.content)}
              </div>
              
              <div className="text-xs opacity-70 mt-2">
                {new Date(message.timestamp).toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-start">
            <div className="ai-chat-bubble ai-assistant-message">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                <span>Analyzing your data<span className="loading-dots"></span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Questions */}
      {messages.length <= 1 && (
        <div className="px-6 py-4 bg-white border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Start Questions:</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickQuestions.map((category, categoryIndex) => (
              <div key={categoryIndex} className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  {category.category}
                </h4>
                {category.questions.map((question, questionIndex) => (
                  <button
                    key={questionIndex}
                    onClick={() => setInputMessage(question)}
                    className="w-full text-left text-sm text-sky-600 hover:text-sky-800 hover:bg-sky-50 p-2 rounded transition-colors duration-200"
                  >
                    {question}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="flex space-x-4">
          <div className="flex-1 relative">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me about your medical data, clinical insights, or research questions..."
              className="form-input resize-none pr-12"
              rows={2}
              disabled={loading}
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || loading}
              className="absolute right-2 bottom-2 p-2 text-sky-600 hover:text-sky-800 disabled:text-gray-400"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <svg className="h-3 w-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>HIPAA Compliant</span>
            </div>
            <div className="flex items-center space-x-1">
              <svg className="h-3 w-3 text-sky-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              <span>Powered by GPT-4</span>
            </div>
          </div>
          <span>Press Enter to send • Shift+Enter for new line</span>
        </div>
      </div>
    </div>
  );
};

export default AIChat;